import { useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../i18n/LanguageProvider";
import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { ShopFloorIcon } from "./ShopFloorIcon";

export interface DeliveryLinePhotoItem {
  id: string;
  name: string;
  imageKey: string | null;
}

interface Props {
  item: DeliveryLinePhotoItem;
  /** This item's photo is uploading (the panel tracks one upload at a time). */
  saving: boolean;
  /** This item's last photo error, if any — shown inside the large view too. */
  error: string | null;
  /** Opens the rear camera for this item; the panel owns the ONE capture flow. */
  onOpenCamera: (item: DeliveryLinePhotoItem) => void;
  /** Closing the large view forgets a stale error. */
  onClearError: (itemId: string) => void;
}

/**
 * G-116 — the photo at the head of a delivery receiving row.
 *
 * With a photo: tapping it opens the photo large, with "Replace photo".
 * Without one: the square says "Add photo" and tapping it opens the rear
 * camera. Either way the shot goes through the SAME flow as the item card
 * (`usePhotoCapture`: resize → background removal → keep the cut-out or the
 * original) and up the SAME route (`POST /shop-mode/items/:id/photo`, which
 * writes ITEM_PHOTO_CHANGED to the Action log). The photo belongs to the item
 * in the catalogue, not to this delivery.
 *
 * The capture flow itself (hidden inputs, review dialog) lives ONCE in the
 * receiving panel, not once per row: a 107-line delivery would otherwise
 * carry 214 hidden file inputs for a dialog only one row can open at a time.
 *
 * Background removal is always on here, with no checkbox (owner ruling
 * 2026-09-28): the review step already offers the original.
 */
export function ShopFloorDeliveryLinePhoto({
  item,
  saving,
  error,
  onOpenCamera,
  onClearError,
}: Props) {
  const { t } = useT();
  const [viewing, setViewing] = useState(false);

  const close = () => {
    setViewing(false);
    onClearError(item.id);
  };

  return (
    <>
      {item.imageKey ? (
        <button
          type="button"
          className="sf-delivery-row__photo sf-delivery-row__photo-button"
          aria-label={t("receiving.viewPhotoOf", { item: item.name })}
          onClick={() => setViewing(true)}
        >
          <ShopFloorBouquetThumb id={item.id} imageKey={item.imageKey} alt={item.name} />
        </button>
      ) : (
        <button
          type="button"
          className="sf-delivery-row__photo sf-delivery-row__photo-button is-empty"
          aria-label={t("receiving.addPhotoOf", { item: item.name })}
          disabled={saving}
          onClick={() => onOpenCamera(item)}
        >
          <ShopFloorIcon name="camera" size={18} />
          <span>{saving ? t("receiving.savingPhoto") : t("receiving.addPhoto")}</span>
        </button>
      )}

      {viewing && item.imageKey ? (
        <PhotoView
          item={{ ...item, imageKey: item.imageKey }}
          saving={saving}
          error={error}
          onReplace={() => onOpenCamera(item)}
          onClose={close}
        />
      ) : null}
    </>
  );
}

function PhotoView({
  item,
  saving,
  error,
  onReplace,
  onClose,
}: {
  item: { id: string; name: string; imageKey: string };
  saving: boolean;
  error: string | null;
  onReplace: () => void;
  onClose: () => void;
}) {
  const { t } = useT();

  useDismissOnEscape(onClose);

  // Portalled: a fixed overlay inside a floor panel is trapped by its
  // backdrop-filter (G-73), and a done row's opacity would dim it.
  return createPortal(
    <div
      className="sf-bouquet-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sf-bouquet-modal sf-delivery-photo-view" onClick={(e) => e.stopPropagation()}>
        <h4 className="sf-bouquet-modal-title">{item.name}</h4>
        <div className="sf-delivery-photo-view__image">
          {/* Keyed on imageKey inside the thumb, so a replace made from this
              view shows here as soon as it lands. */}
          <ShopFloorBouquetThumb
            id={item.id}
            imageKey={item.imageKey}
            alt={item.name}
            size="full"
          />
        </div>
        {error ? <div className="alert error">{error}</div> : null}
        <div className="button-row sf-delivery-photo-view__actions">
          <button type="button" className="button plain" onClick={onClose}>
            {t("common.close")}
          </button>
          <button type="button" className="button tinted" disabled={saving} onClick={onReplace}>
            <ShopFloorIcon name="camera" size={18} />
            {saving ? t("receiving.savingPhoto") : t("receiving.replacePhoto")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
